import React, {PropsWithChildren, useEffect, useRef, useState} from "react";
import {observer} from "mobx-react";
import styled from "styled-components";
import {CardBox} from "../../basic-components/card/card.box";
import {XCloseSmallIcon} from "../../assets/svg/x-close-small.icon";
import ReactDOM from "react-dom";
import {useServices} from "../../hooks/use-services.hook";
import {createPopper} from "@popperjs/core";
import {Instance as PopperInstance} from "@popperjs/core/lib/types";
import {Placement} from "@popperjs/core/lib/enums";
import {isClickInsideElement} from "../../utils/is-click-inside-element";
import {
    TooltipArrowPointingDownBox,
    TooltipArrowPointingLeftBox,
    TooltipArrowPointingRightBox,
    TooltipArrowPointingUpBox
} from "./tooltip-arrows";
import {CSS_TOOLTIP_ARROW_SIZE, CSS_VAR_TOOLTIP_BACKGROUND_COLOR, CSS_VAR_TOOLTIP_COLOR} from "./tootip-css-constants";
import {ZIndex} from "../../types/z-index";



const TooltipContainerBox = styled(CardBox)`
    position: relative;
    ${CSS_VAR_TOOLTIP_BACKGROUND_COLOR}: ${props => props.theme.colors.cardBackground};
    ${CSS_VAR_TOOLTIP_COLOR}: ${props => props.theme.colors.cardColor};
    display: flex;
    flex-direction: column;
    padding: 0;
    z-index: ${ZIndex.DropDownPopper};
  
`

const TooltipHeaderBox = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    width: 100%;
    padding: ${props => props.theme.spacing.spacing16} ${props => props.theme.spacing.spacing24} 0 0;
`
const TooltipXCloseButtonBox = styled.div`
    cursor: pointer;
`

const TooltipBodyBox = styled.div`
    
`

interface CreatePopperOptions {
    placement: Placement;
    targetEl: HTMLElement | null;
    popperEl: HTMLElement | null;
    arrowEl: HTMLElement | null;
}

function createTooltipPopper(options: CreatePopperOptions) {
    if(!(options.targetEl && options.popperEl)) {
        return null;
    }

    let modifiers: any[];

    if(options.arrowEl) {
        modifiers =  [
            {
                name: 'offset',
                options: {
                    offset: [0, CSS_TOOLTIP_ARROW_SIZE]
                }
            },
            {
                name: 'arrow',
                options: {
                    element: options.arrowEl
                }
            }
        ]
    } else {
        modifiers =  [
            {
                name: 'offset',
                options: {
                    offset: [0, 6]
                }
            }
        ]
    }

    return createPopper(options.targetEl, options.popperEl, {
        placement: options.placement,
        strategy: 'absolute',
        modifiers: modifiers
    });
}

export interface ITooltipController {
    open(): void;
    close(): void;
}

export enum TooltipToggleBehaviorEnum {
    /**
     * When the mouse enters the target element the tool tip is shown and when the mouse leaves target element
     * On mobile mouse enter occurs when the element is clicked and the tooltip is closed when user clicks somewhere outside.
     */
    OnTargetMouseEnterLeave,
    /**
     * The tooltip is shown/closed when the target element is clicked
     */
    OnTargetClick
}



interface TooltipComponentProps extends PropsWithChildren {
    targetRef: React.MutableRefObject<HTMLElement | null>;
    placement: Placement;
    toggleBehavior: TooltipToggleBehaviorEnum;
    showCloseButton?: boolean;
    tooltipControllerRef?: React.MutableRefObject<ITooltipController | null>;
    className?: string;
    hideArrow?: boolean;
}

export const TooltipComponent: React.FC<TooltipComponentProps> = observer((props) => {
    const services = useServices();
    const popperContainerRef = useRef<HTMLDivElement | null>(null);
    const popperArrowRef = useRef<HTMLDivElement | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const tooltipControllerRef = useRef<ITooltipController>({
        open: () => {
            setIsOpen(true)
        },
        close: () => {
            setIsOpen(false)
        }
    });

    if(props.tooltipControllerRef) {
        props.tooltipControllerRef.current = tooltipControllerRef.current;
    }

    useEffect(() => {
        let popperInstance: PopperInstance | null = null;
        if(isOpen) {
            popperInstance = createTooltipPopper({
                placement: props.placement,
                targetEl: props.targetRef.current,
                popperEl: popperContainerRef.current,
                arrowEl: popperArrowRef.current,
            });
        }

        return () => {
            if(popperInstance) {
                popperInstance.destroy();
            }
        };
    }, [isOpen, props.targetRef, props.placement]);

    useEffect(() => {
        const onDocumentClick = (event: MouseEvent) => {
            if(!popperContainerRef.current || !props.targetRef.current) {
                return;
            }
            if(!isClickInsideElement(event, popperContainerRef.current)
                && !isClickInsideElement(event, props.targetRef.current)) {
                tooltipControllerRef.current.close();
            }
        }
        document.addEventListener('click', onDocumentClick);

        return () => {
            document.removeEventListener('click', onDocumentClick);
        }
    }, [isOpen, props.targetRef]);

    useEffect(() => {
        const onTargetMouseEnter = () => {
            tooltipControllerRef.current.open();
        }

        const onTargetMouseLeave = () => {
            tooltipControllerRef.current.close();
        }

        const onTargetClick = () => {
            setIsOpen(!isOpen);
        }

        if(props.toggleBehavior === TooltipToggleBehaviorEnum.OnTargetMouseEnterLeave) {
            props.targetRef.current?.addEventListener('mouseenter', onTargetMouseEnter);
            props.targetRef.current?.addEventListener('mouseleave', onTargetMouseLeave);
        } else {
            props.targetRef.current?.addEventListener('click', onTargetClick);
        }


        return () => {
            if(props.toggleBehavior === TooltipToggleBehaviorEnum.OnTargetMouseEnterLeave) {
                props.targetRef.current?.removeEventListener('mouseenter', onTargetMouseEnter);
                props.targetRef.current?.removeEventListener('mouseleave', onTargetMouseLeave);
            } else {
                props.targetRef.current?.removeEventListener('click', onTargetClick);
            }

        }

    }, [isOpen, props.targetRef, props.toggleBehavior]);

    if(!isOpen) {
        return null;
    }

    const onClose = () => {
        tooltipControllerRef.current.close();
    }

    const renderHeader = () => {
        if(!props.showCloseButton) {
            return null;
        }

        return (
            <TooltipHeaderBox>
                <TooltipXCloseButtonBox onClick={onClose}>
                    <XCloseSmallIcon size={0.8}/>
                </TooltipXCloseButtonBox>
            </TooltipHeaderBox>
        );
    }


    const renderArrow = () => {
        if(props.hideArrow) {
            return null;
        }

        switch (props.placement) {
            case 'top':
            case 'top-start':
            case 'top-end':
                return <TooltipArrowPointingDownBox ref={popperArrowRef}/>;
            case 'bottom':
            case 'bottom-start':
            case 'bottom-end':
                return <TooltipArrowPointingUpBox ref={popperArrowRef}/>;
            case 'left':
            case 'left-start':
            case 'left-end':
                return <TooltipArrowPointingRightBox ref={popperArrowRef}/>;
            case 'right':
            case 'right-start':
            case 'right-end':
                return <TooltipArrowPointingLeftBox ref={popperArrowRef}/>;
            default:
                return null
        }
    }


    return ReactDOM.createPortal(
        <TooltipContainerBox ref={popperContainerRef} className={props.className}>
            {renderArrow()}
            {renderHeader()}
            <TooltipBodyBox>
                {props.children}
            </TooltipBodyBox>
        </TooltipContainerBox>
    , services.document.body)
})